import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Coins, CreditCard, Loader2, Heart, Copy, Check, X, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export function TopUpCard({ compact = false }) {
  const { user } = useAuth();
  const { t } = useT();
  const [opts, setOpts] = useState(null);
  const [plan, setPlan] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/payments/topup-options").then(({ data }) => setOpts(data)).catch(() => setOpts({ available: false, plans: [] }));
  }, [user?.initial_topup_used]);

  if (!opts) return null;
  if (!opts.available) {
    if (compact) return null;
    return <p className="text-sm text-[#5f6b62] bg-[#f4f0e8] rounded-xl px-4 py-3" data-testid="topup-used-note">{t("topup_used")}</p>;
  }

  const pay = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/payments/signup-checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border-2 border-[#f3d98a] card-shadow p-5" data-testid="topup-card">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-[#d97706]" />
        <h2 className="font-head font-bold text-lg">{t("topup_title")}</h2>
      </div>
      <p className="text-sm text-[#5f6b62] mt-1">{t("topup_sub")}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4" data-testid="topup-plans">
        {opts.plans.map((p) => (
          <button key={p.euro} type="button" onClick={() => setPlan(p.euro)} data-testid={`topup-plan-${p.euro}`}
            className={`p-3 rounded-xl border-2 text-left transition-all ${plan === p.euro ? "border-[#d97706] bg-[#fef3c7]" : "border-[#e5dfd3] hover:border-[#d97706]"}`}>
            <div className="font-head font-extrabold text-xl text-[#1e3326]">{p.euro} €</div>
            <div className="font-mono font-bold text-[#92600a] text-sm flex items-center gap-1"><Coins className="w-3.5 h-3.5" /> {p.deuro} D€</div>
          </button>
        ))}
      </div>
      {error && <div className="text-sm text-[#d9532f] mt-3" data-testid="topup-error">{error}</div>}
      <button onClick={pay} disabled={loading} data-testid="topup-pay-btn"
        className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#d97706] text-white font-semibold hover:bg-[#c26806] disabled:opacity-60 transition-all">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-4 h-4" /> {t("topup_btn")} · {plan} €</>}
      </button>
      <p className="text-[11px] text-[#5f6b62] text-center mt-2">{t("topup_once")}</p>
    </motion.div>
  );
}

export function DonateButton({ className = "" }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="donate-btn"
        className={`flex items-center justify-center gap-2 px-5 py-3 rounded-xl border-2 border-[#d9532f] text-[#d9532f] font-semibold hover:bg-[#fdeae5] transition-all ${className}`}>
        <Heart className="w-4 h-4" /> {t("donate_btn")}
      </button>
      {open && <DonateModal onClose={() => setOpen(false)} />}
    </>
  );
}

function DonateModal({ onClose }) {
  const { t } = useT();
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState("");

  useEffect(() => {
    api.get("/donations/info").then(({ data }) => setInfo(data)).catch(() => setInfo({ iban: "", account_holder: "" }));
  }, []);

  const copy = async (k, v) => {
    try { await navigator.clipboard.writeText(v); setCopied(k); setTimeout(() => setCopied(""), 1500); } catch { /* ignore */ }
  };

  const Row = ({ k, label, value }) => (
    <div className="flex items-center justify-between gap-3 bg-[#f4f0e8] rounded-xl px-4 py-3">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-[#5f6b62]">{label}</p>
        <p className="font-mono font-semibold text-sm break-all" data-testid={`donate-${k}`}>{value}</p>
      </div>
      <button onClick={() => copy(k, value)} data-testid={`donate-copy-${k}`} className="shrink-0 w-9 h-9 rounded-lg border border-[#e5dfd3] flex items-center justify-center hover:bg-white" title={t("copy")}>
        {copied === k ? <Check className="w-4 h-4 text-[#059669]" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="donate-modal">
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-2xl p-6 card-shadow">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-head text-xl font-bold flex items-center gap-2"><Heart className="w-5 h-5 text-[#d9532f]" /> {t("donate_title")}</h3>
          <button onClick={onClose} data-testid="donate-close" className="text-[#5f6b62]"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-[#5f6b62] mb-4">{t("donate_sub")}</p>
        {info === null ? null : !info.iban ? (
          <p className="text-sm text-[#92600a] bg-[#fef3c7] rounded-xl px-4 py-3" data-testid="donate-not-configured">{t("donate_not_configured")}</p>
        ) : (
          <div className="space-y-2">
            {info.account_holder && <Row k="holder" label={t("donate_holder")} value={info.account_holder} />}
            <Row k="iban" label={t("donate_iban")} value={info.iban} />
            <Row k="reason" label={t("donate_reason")} value={t("donate_reason_value")} />
          </div>
        )}
      </motion.div>
    </div>
  );
}
